import { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";

const SERVICE_ID = "service_htfcyrb";
const TEMPLATE_ID = "template_z41pzab";
const PUBLIC_KEY = "2hX5B-7ElfjEoGjSM";

export const Contact = () => {
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });

  const onChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Por favor completa los campos obligatorios.");
      return;
    }
    setLoading(true);
    try {
      await emailjs.sendForm(
        SERVICE_ID,
        TEMPLATE_ID,
        formRef.current,
        { publicKey: PUBLIC_KEY },
      );
      toast.success("¡Mensaje enviado! Te contactaremos muy pronto.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      console.error("EmailJS error:", err);
      toast.error("No pudimos enviar tu mensaje. Inténtalo nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="bg-white py-24 sm:py-32 border-t border-[#BFBFBF]/40"
    >
      <Toaster position="top-center" richColors />
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: Info */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#BF1828] font-medium">
              Contacto
            </div>
            <h2
              data-testid="contact-heading"
              className="mt-5 font-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1] text-black"
            >
              Hablemos de
              <br />
              tu próximo
              <br />
              <span className="text-[#BF1828]">proyecto.</span>
            </h2>
            <p className="mt-8 font-body text-base sm:text-lg leading-relaxed text-[#595959] max-w-md">
              Compártenos los detalles y un asesor te contactará para diseñar
              una estrategia a la medida.
            </p>

            <div className="mt-12 space-y-7">
              <div className="flex items-start gap-5" data-testid="contact-info-address">
                <div className="w-11 h-11 border border-black flex items-center justify-center shrink-0">
                  <MapPin size={18} strokeWidth={1.6} className="text-[#BF1828]" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-[#595959]">
                    Dirección
                  </div>
                  <div className="mt-1 font-body text-[15px] text-black leading-relaxed max-w-xs">
                    Calle Jorge Santayana 4963, int. Z, C.P. col. Jardines
                    Universidad, C.P. 45110, Zapopan, Jalisco.
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-5" data-testid="contact-info-phone">
                <div className="w-11 h-11 border border-black flex items-center justify-center shrink-0">
                  <Phone size={18} strokeWidth={1.6} className="text-[#BF1828]" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-[#595959]">
                    Teléfono
                  </div>
                  <a
                    href="tel:+523310685211"
                    className="mt-1 block font-body text-[15px] text-black hover:text-[#BF1828] transition-colors"
                  >
                    33 1 068 5211
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-5" data-testid="contact-info-email">
                <div className="w-11 h-11 border border-black flex items-center justify-center shrink-0">
                  <Mail size={18} strokeWidth={1.6} className="text-[#BF1828]" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-[#595959]">
                    Email
                  </div>
                  <a
                    href="mailto:contacto@simplesolutionsasesores.com"
                    className="mt-1 block font-body text-[15px] text-black hover:text-[#BF1828] transition-colors break-all"
                  >
                    contacto@simplesolutionsasesores.com
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <form
              ref={formRef}
              onSubmit={onSubmit}
              noValidate
              data-testid="contact-form"
              className="bg-[#F9F9F9] p-8 sm:p-12 border border-[#BFBFBF]/60"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8">
                <div className="sm:col-span-1">
                  <label
                    htmlFor="name"
                    className="block text-[11px] uppercase tracking-[0.25em] text-[#595959] mb-2"
                  >
                    Nombre <span className="text-[#BF1828]">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={onChange}
                    data-testid="contact-input-name"
                    className="w-full bg-transparent border-b-2 border-[#BFBFBF] focus:border-[#BF1828] focus:ring-0 focus:outline-none px-0 py-3 text-base text-black placeholder:text-[#BFBFBF] transition-colors"
                    placeholder="Tu nombre completo"
                  />
                </div>

                <div className="sm:col-span-1">
                  <label
                    htmlFor="email"
                    className="block text-[11px] uppercase tracking-[0.25em] text-[#595959] mb-2"
                  >
                    Email <span className="text-[#BF1828]">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={onChange}
                    data-testid="contact-input-email"
                    className="w-full bg-transparent border-b-2 border-[#BFBFBF] focus:border-[#BF1828] focus:ring-0 focus:outline-none px-0 py-3 text-base text-black placeholder:text-[#BFBFBF] transition-colors"
                    placeholder="tu@empresa.com"
                  />
                </div>

                <div className="sm:col-span-1">
                  <label
                    htmlFor="phone"
                    className="block text-[11px] uppercase tracking-[0.25em] text-[#595959] mb-2"
                  >
                    Teléfono
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={onChange}
                    data-testid="contact-input-phone"
                    className="w-full bg-transparent border-b-2 border-[#BFBFBF] focus:border-[#BF1828] focus:ring-0 focus:outline-none px-0 py-3 text-base text-black placeholder:text-[#BFBFBF] transition-colors"
                    placeholder="33 1234 5678"
                  />
                </div>

                <div className="sm:col-span-1">
                  <label
                    htmlFor="company"
                    className="block text-[11px] uppercase tracking-[0.25em] text-[#595959] mb-2"
                  >
                    Empresa
                  </label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    value={form.company}
                    onChange={onChange}
                    data-testid="contact-input-company"
                    className="w-full bg-transparent border-b-2 border-[#BFBFBF] focus:border-[#BF1828] focus:ring-0 focus:outline-none px-0 py-3 text-base text-black placeholder:text-[#BFBFBF] transition-colors"
                    placeholder="Nombre de tu empresa"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label
                    htmlFor="message"
                    className="block text-[11px] uppercase tracking-[0.25em] text-[#595959] mb-2"
                  >
                    Mensaje <span className="text-[#BF1828]">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={onChange}
                    data-testid="contact-input-message"
                    className="w-full bg-transparent border-b-2 border-[#BFBFBF] focus:border-[#BF1828] focus:ring-0 focus:outline-none px-0 py-3 text-base text-black placeholder:text-[#BFBFBF] transition-colors resize-none"
                    placeholder="Cuéntanos sobre tu proyecto o necesidad..."
                  />
                </div>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <p className="text-[12px] text-[#595959] max-w-sm">
                  Al enviar este formulario aceptas que un miembro del equipo
                  se ponga en contacto contigo.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="group inline-flex items-center justify-center gap-3 bg-[#BF1828] text-white hover:bg-black transition-all duration-300 px-8 py-4 text-sm uppercase tracking-[0.2em] font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      Enviar Mensaje
                      <Send
                        size={16}
                        className="transition-transform group-hover:translate-x-1"
                      />
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
